import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const DevModeContext = createContext(null);

// Environment configuration
const ENV_CONFIG = {
  production: {
    apiDomain: process.env.REACT_APP_API_DOMAIN_PROD,
  },
  development: {
    apiDomain: process.env.REACT_APP_API_DOMAIN_DEV,
  },
};

const CLICK_THRESHOLD = 10;
const CLICK_TIMEOUT_MS = 3000; // Reset counter if clicks are too slow

export function DevModeProvider({ children }) {
  const [isDevMode, setIsDevMode] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [lastClickTime, setLastClickTime] = useState(0);

  const handleLogoClick = useCallback(() => {
    const now = Date.now();

    // Reset counter if too much time has passed since last click
    if (now - lastClickTime > CLICK_TIMEOUT_MS) {
      setClickCount(1);
      setLastClickTime(now);
      return;
    }

    const newCount = clickCount + 1;
    setClickCount(newCount);
    setLastClickTime(now);

    if (newCount >= CLICK_THRESHOLD) {
      setIsDevMode((prev) => !prev);
      setClickCount(0);
    }
  }, [clickCount, lastClickTime]);

  const currentConfig = useMemo(() => {
    const mode = isDevMode ? 'development' : 'production';
    return ENV_CONFIG[mode];
  }, [isDevMode]);

  const value = useMemo(() => ({
    isDevMode,
    setIsDevMode,
    handleLogoClick,
    clickCount,
    currentConfig,
    apiDomain: currentConfig.apiDomain,
  }), [isDevMode, handleLogoClick, clickCount, currentConfig]);

  return (
    <DevModeContext.Provider value={value}>
      {children}
    </DevModeContext.Provider>
  );
}

export function useDevMode() {
  const context = useContext(DevModeContext);
  if (!context) {
    throw new Error('useDevMode must be used within a DevModeProvider');
  }
  return context;
}

export default DevModeContext;
